import type { ReactNode } from 'react';
import { memo, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useObservable } from 'react-use-observable';

import { ArrowDownOutlined, ArrowUpOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Spin, Table, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import Truncate from 'v2/components/Shared/TruncateText';
import { Mask } from 'v2/components/Shared/ValuesVisibility';
import { numberToBRL } from 'v2/helpers/money';
import type { IFinancialInvoice } from 'v2/interfaces/financial';
import type { IUserData } from 'v2/interfaces/user';
import eduzzAccountsService from 'v2/services/eduzzAccount';
import { financialStatementBlockedService } from 'v2/services/financialStatement/blocked';
import { financialStatementService } from 'v2/services/financialStatement/statement';

import PaginationLoadMore from './PaginationLoadMore';

interface IProps {
  transactionDate: string;
  showBlockedDetails?: boolean;
}

interface IInvoiceDetailsProps {
  invoiceId: number;
  className?: string;
  children: ReactNode;
  idPrefix: string;
  user: IUserData;
}

function InvoiceDetailsLink({ invoiceId, className, idPrefix, children }: IInvoiceDetailsProps) {
  return (
    <Link
      to={`/producer/invoice/details/${invoiceId}`}
      id={`${idPrefix}-${invoiceId}`}
      className={className ? className : ''}
      target='_blank'
      rel='noopener noreferrer'
    >
      {children}
    </Link>
  );
}

function ContentInnerTable({ transactionDate, showBlockedDetails }: IProps) {
  const [isMobile, setIsMobile] = useState(() => window.matchMedia('(max-width: 599.95px)').matches);
  const [user] = useObservable(() => eduzzAccountsService.getUser(), []);
  const [filters] = useObservable(() => financialStatementService.getFilters(), []);

  useEffect(() => {
    const mql = window.matchMedia('(max-width: 599.95px)');
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  useObservable(
    () =>
      showBlockedDetails
        ? financialStatementBlockedService.loadDetails(transactionDate)
        : financialStatementService.loadDetails(transactionDate),
    [
      transactionDate,
      showBlockedDetails,
      filters?.movement_type,
      filters?.transaction_type,
      filters?.search_type,
      filters?.search_text
    ]
  );

  const [currEntries, setCurrEntries] = useState<IFinancialInvoice[] | null>(null);
  const [currPage, setCurrPage] = useState(1);

  const [isLoading] = useObservable(
    () =>
      showBlockedDetails
        ? financialStatementBlockedService.isLoadingDetails()
        : financialStatementService.isLoadingDetails(),
    [showBlockedDetails]
  );
  const [pagination] = useObservable(
    () =>
      showBlockedDetails
        ? financialStatementBlockedService.getPaginationResponseDetails()
        : financialStatementService.getPaginationResponseDetails(),
    [showBlockedDetails]
  );
  const [entries] = useObservable(
    () =>
      showBlockedDetails
        ? financialStatementBlockedService.getFinancialStatementBlockedDetails()
        : financialStatementService.getFinancialStatementDetails(),
    [showBlockedDetails]
  );

  useEffect(() => {
    if (!entries) {
      setCurrEntries(null);
      setCurrPage(1);
      return;
    }

    const nextEntries = entries as unknown as IFinancialInvoice[];

    if (!pagination?.current_page || pagination.current_page <= 1) {
      setCurrEntries([...nextEntries]);
      setCurrPage(1);
      return;
    }

    if (pagination.current_page > currPage) {
      setCurrEntries(prev => [...(prev || []), ...nextEntries]);
      setCurrPage(pagination.current_page);
    }
  }, [entries, pagination, currPage]);

  const columns: ColumnsType<IFinancialInvoice> = [
    {
      key: 'icon',
      width: 30,
      render: (_, entry) => {
        const isPositive = entry.amount > 0;
        return isPositive && !showBlockedDetails ? (
          <ArrowDownOutlined className='text-success!' />
        ) : (
          <ArrowUpOutlined />
        );
      }
    },
    {
      title: 'Fatura',
      key: 'invoice',
      width: 40,
      align: 'center',
      render: (_, entry) => (
        <div className='flex max-w-[50px] justify-center max-sm:max-w-[70px] max-sm:flex-col'>
          {entry.invoice_id ? (
            <InvoiceDetailsLink user={user} invoiceId={entry.invoice_id} idPrefix='invoice'>
              {entry.invoice_id}
            </InvoiceDetailsLink>
          ) : (
            '-'
          )}
        </div>
      )
    },
    {
      title: 'Histórico',
      key: 'description',
      render: (_, entry) => (
        <div className='flex items-center gap-1 max-sm:flex-col'>
          <Truncate count={isMobile ? 25 : 70} final='...'>
            {showBlockedDetails && entry.is_chargeback ? `[CHARGEBACK] - ${entry.description}` : entry.description}
          </Truncate>
          {entry.is_installment_settlement && (
            <Tooltip
              placement='top'
              title='Parte do valor da Liquidação do Parcelamento é repassado de volta para você'
            >
              <InfoCircleOutlined style={{ fontSize: 14 }} />
            </Tooltip>
          )}
        </div>
      )
    },
    {
      title: 'Valor',
      key: 'amount',
      width: 150,
      align: isMobile ? 'left' : 'right',
      render: (_, entry) => {
        const isPositive = entry.amount > 0;
        const amountSign = isPositive && !showBlockedDetails ? '+' : '-';
        return (
          <span className={isPositive && !showBlockedDetails ? 'text-success' : undefined}>
            {`${amountSign} `}
            <Mask>{`${numberToBRL(isPositive ? entry.amount : entry.amount * -1)}`}</Mask>
          </span>
        );
      }
    }
  ];

  if (!currEntries) {
    return (
      <div className='flex items-center justify-center p-5'>
        <Spin />
      </div>
    );
  }

  return (
    <div>
      <Table
        columns={columns}
        dataSource={currEntries}
        rowKey={(record: IFinancialInvoice, index: number) => `Row-${record.invoice_id}-${index}`}
        rowClassName='leading-[150%] shadow-none!'
        pagination={false}
        loading={isLoading}
        className='[&_td]:text-grey-500 [&_td]:p-2.5 [&_td]:text-sm [&_td]:uppercase [&_th]:p-2.5! [&_th]:text-sm [&_th]:font-bold [&_th]:text-black'
      />

      <PaginationLoadMore isLoading={isLoading} pagination={pagination} showBlockedDetails={showBlockedDetails} />
    </div>
  );
}

export default memo(ContentInnerTable);
